import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert, Autocomplete, Box, Button, Dialog, DialogActions, DialogContent, DialogTitle,
  Grid, IconButton, MenuItem, Paper, Table, TableBody, TableCell, TableHead, TableRow,
  TextField, ToggleButton, ToggleButtonGroup, Typography
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import { useNavigate } from 'react-router-dom';
import { createCustomer, createRental, getCustomers, getProducts, searchSerials } from '../../services/api';
import { getErrorMessage } from '../../utils/errorMessage';
import { addCycle, durationLabel, invoiceRhythm, periodEnd, rateLabel } from '../../utils/rentalBilling';
import { money, RentalPageHeader, RentalSection, tableSx } from './rentalUi';

const emptyNewCustomer = {
  name: '',
  phone: '',
  email: '',
  company_name: '',
  address: ''
};

const today = () => new Date().toISOString().slice(0, 10);

const RentalForm = () => {
  const navigate = useNavigate();
  const [customers, setCustomers] = useState([]);
  const [products, setProducts] = useState([]);
  const [customer, setCustomer] = useState(null);
  const [billingCycle, setBillingCycle] = useState('monthly');
  const [duration, setDuration] = useState(1);
  const [startDate, setStartDate] = useState(today());
  const [deposit, setDeposit] = useState(0);
  const [notes, setNotes] = useState('');
  const [rentalSource, setRentalSource] = useState('sun_systems');
  const [outsideItem, setOutsideItem] = useState('');
  const [outsidePurchaseDate, setOutsidePurchaseDate] = useState('');
  const [outsidePurchasePrice, setOutsidePurchasePrice] = useState('');
  const [outsideAssetValue, setOutsideAssetValue] = useState('');
  const [outsideRent, setOutsideRent] = useState('');
  const [outsideCycle, setOutsideCycle] = useState('monthly');
  const [outsideDuration, setOutsideDuration] = useState(1);
  const [outsideComponents, setOutsideComponents] = useState('');
  const [items, setItems] = useState([]);
  const [serialQuery, setSerialQuery] = useState('');
  const [serialHits, setSerialHits] = useState([]);
  const [pickedProduct, setPickedProduct] = useState(null);
  const [manualSerial, setManualSerial] = useState('');
  const [periodRate, setPeriodRate] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [newOpen, setNewOpen] = useState(false);
  const [newCustomer, setNewCustomer] = useState(emptyNewCustomer);
  const [savingCustomer, setSavingCustomer] = useState(false);

  const loadCustomers = async () => {
    try {
      const res = await getCustomers();
      setCustomers(res.data || res || []);
    } catch (_) {
      setCustomers([]);
    }
  };

  useEffect(() => {
    loadCustomers();
    getProducts({ limit: 300 })
      .then((res) => setProducts(res.data || []))
      .catch(() => setProducts([]));
  }, []);

  const saveNewCustomer = async () => {
    if (!newCustomer.name.trim()) {
      setError('Customer name is required');
      return;
    }
    try {
      setSavingCustomer(true);
      setError('');
      const res = await createCustomer({
        name: newCustomer.name.trim(),
        phone: newCustomer.phone.trim() || null,
        email: newCustomer.email.trim() || null,
        company_name: newCustomer.company_name.trim() || null,
        address: newCustomer.address.trim() || null,
        customer_type: 'individual',
        customer_code: `RC-${Date.now().toString().slice(-6)}`
      });
      const created = res.data || res;
      await loadCustomers();
      if (created?.id) setCustomer(created);
      setNewCustomer(emptyNewCustomer);
      setNewOpen(false);
      setSuccess('Customer added');
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError(getErrorMessage(err, 'Failed to add customer'));
    } finally {
      setSavingCustomer(false);
    }
  };

  useEffect(() => {
    const q = serialQuery.trim();
    if (q.length < 3) {
      setSerialHits([]);
      return undefined;
    }
    const t = setTimeout(() => {
      searchSerials(q)
        .then((res) => setSerialHits(res.data || []))
        .catch(() => setSerialHits([]));
    }, 250);
    return () => clearTimeout(t);
  }, [serialQuery]);

  const endDate = useMemo(() => {
    if (!startDate || !duration) return '';
    return periodEnd(addCycle(startDate, billingCycle, Math.max(1, duration) - 1), billingCycle);
  }, [startDate, duration, billingCycle]);

  const periodTotal = items.reduce((s, it) => s + Number(it.period_rate || 0) * (it.quantity || 1), 0);
  const contractTotal = periodTotal * Math.max(1, duration);

  const addItem = (hit) => {
    const serial = (hit?.serial_number || manualSerial).trim();
    const product = hit?.product || pickedProduct;
    const rate = parseFloat(periodRate);
    if (!Number.isFinite(rate) || rate < 0) {
      setError('Enter the rate for each billing period');
      return;
    }
    if (serial && items.some((it) => it.serial_number === serial)) {
      setError('That serial is already on this rental');
      return;
    }

    if (rentalSource === 'outside') {
      const name = outsideItem.trim() || product?.name || '';
      if (!name) {
        setError('Enter the item name');
        return;
      }
      if (!serial) {
        setError('Enter the serial number');
        return;
      }
      if (!outsidePurchaseDate) {
        setError('Enter the purchase date');
        return;
      }
      const buy = parseFloat(outsidePurchasePrice);
      const asset = parseFloat(outsideAssetValue);
      const supplierRent = parseFloat(outsideRent);
      if (!Number.isFinite(buy) || buy < 0) {
        setError('Enter the purchase price');
        return;
      }
      if (!Number.isFinite(asset) || asset < 0) {
        setError('Enter the asset value');
        return;
      }
      if (!Number.isFinite(supplierRent) || supplierRent < 0) {
        setError('Enter the outside rent (what we pay)');
        return;
      }
      setItems((prev) => [
        ...prev,
        {
          product_id: null,
          product_code: 'OUT-RENT',
          product_name: name,
          serial_number: serial,
          purchase_date: outsidePurchaseDate,
          purchase_price: buy,
          asset_value: asset,
          outside_rent: supplierRent,
          outside_cycle: outsideCycle,
          outside_duration: Math.max(1, parseInt(outsideDuration, 10) || 1),
          components: outsideComponents.trim(),
          quantity: 1,
          period_rate: rate,
          customer_price: rate
        }
      ]);
      setOutsideItem('');
      setOutsidePurchaseDate('');
      setOutsidePurchasePrice('');
      setOutsideAssetValue('');
      setOutsideRent('');
      setOutsideComponents('');
      setManualSerial('');
      setPeriodRate('');
      setError('');
      return;
    }

    if (!serial && !product) {
      setError('Enter a serial number or choose a product');
      return;
    }
    setItems((prev) => [
      ...prev,
      {
        product_id: product?.id || hit?.product_id || null,
        product_code: product?.product_code || '',
        product_name: product?.name || serial || 'Rental item',
        serial_number: serial,
        quantity: 1,
        period_rate: rate,
        serial_status: hit?.status || ''
      }
    ]);
    setSerialQuery('');
    setSerialHits([]);
    setManualSerial('');
    setPickedProduct(null);
    setPeriodRate('');
    setError('');
  };

  const save = async () => {
    if (!customer?.id) {
      setError('Select a customer');
      return;
    }
    if (!items.length) {
      setError('Add at least one item');
      return;
    }
    try {
      setSaving(true);
      setError('');
      const res = await createRental({
        customer_id: customer.id,
        billing_cycle: billingCycle,
        duration_count: Math.max(1, parseInt(duration, 10) || 1),
        start_date: startDate,
        deposit: parseFloat(deposit) || 0,
        notes,
        rental_source: rentalSource,
        items
      });
      const id = res.data?.id;
      navigate(id ? `/rentals/${id}` : '/rentals');
    } catch (err) {
      setError(getErrorMessage(err, 'Failed to create rental'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Box>
      <RentalPageHeader
        backTo="/rentals"
        backLabel="All rentals"
        title="Create rental"
        subtitle={`Choose Sun Systems stock or an outside rental. Billing can be daily, weekly, or monthly — ${invoiceRhythm(billingCycle)}.`}
      />

      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}
      {success && <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess('')}>{success}</Alert>}

      <RentalSection title="Contract">
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 0.5 }}>
              Rental type
            </Typography>
            <ToggleButtonGroup
              exclusive
              value={rentalSource}
              onChange={(_, v) => {
                if (!v) return;
                setRentalSource(v);
                setItems([]);
                setError('');
                if (v === 'outside') {
                  setOutsideCycle(billingCycle);
                  setOutsideDuration(duration);
                }
              }}
              size="small"
              sx={{
                '& .MuiToggleButton-root': {
                  px: 2, py: 0.75, textTransform: 'none', fontWeight: 700, borderRadius: '8px !important'
                }
              }}
            >
              <ToggleButton value="sun_systems">Sun Systems</ToggleButton>
              <ToggleButton value="outside">Rent from Outside</ToggleButton>
            </ToggleButtonGroup>
          </Grid>
          <Grid item xs={12} md={6}>
            <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-start' }}>
              <Autocomplete
                sx={{ flex: 1 }}
                options={customers}
                getOptionLabel={(c) => c ? `${c.name}${c.phone ? ` · ${c.phone}` : ''}` : ''}
                value={customer}
                onChange={(_, v) => setCustomer(v)}
                renderInput={(params) => <TextField {...params} label="Customer" required />}
              />
              <Button
                variant="outlined"
                startIcon={<PersonAddIcon />}
                onClick={() => setNewOpen(true)}
                sx={{ mt: 0.5, whiteSpace: 'nowrap' }}
              >
                New customer
              </Button>
            </Box>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <TextField
              select
              fullWidth
              label="Billing"
              value={billingCycle}
              onChange={(e) => setBillingCycle(e.target.value)}
            >
              <MenuItem value="daily">Daily</MenuItem>
              <MenuItem value="weekly">Weekly</MenuItem>
              <MenuItem value="monthly">Monthly</MenuItem>
            </TextField>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <TextField
              type="number"
              fullWidth
              label={durationLabel(billingCycle)}
              value={duration}
              onChange={(e) => setDuration(Math.max(1, parseInt(e.target.value, 10) || 1))}
              inputProps={{ min: 1 }}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <TextField
              type="date"
              fullWidth
              label="Start date"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
              InputLabelProps={{ shrink: true }}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <TextField fullWidth label="End date" value={endDate} InputProps={{ readOnly: true }} />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <TextField
              type="number"
              fullWidth
              label="Deposit"
              value={deposit}
              onChange={(e) => setDeposit(e.target.value)}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label="Notes"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
            />
          </Grid>
        </Grid>
      </RentalSection>

      <RentalSection title={rentalSource === 'outside' ? 'Outside rental items' : 'Sun Systems items'}>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          {rentalSource === 'outside'
            ? 'Enter purchase date, purchase price, asset value, outside rent term/cost, and the customer price we charge.'
            : 'Look up a Sun Systems serial or pick a product from stock.'}
        </Typography>
        <Grid container spacing={2} alignItems="center">
          {rentalSource === 'outside' ? (
            <>
              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth
                  required
                  label="Item"
                  value={outsideItem}
                  onChange={(e) => setOutsideItem(e.target.value)}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <TextField
                  fullWidth
                  required
                  label="Serial number"
                  value={manualSerial}
                  onChange={(e) => setManualSerial(e.target.value)}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <TextField
                  type="date"
                  fullWidth
                  required
                  label="Purchase date"
                  value={outsidePurchaseDate}
                  onChange={(e) => setOutsidePurchaseDate(e.target.value)}
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <TextField
                  type="number"
                  fullWidth
                  required
                  label="Purchase price"
                  value={outsidePurchasePrice}
                  onChange={(e) => setOutsidePurchasePrice(e.target.value)}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <TextField
                  type="number"
                  fullWidth
                  required
                  label="Asset value"
                  value={outsideAssetValue}
                  onChange={(e) => setOutsideAssetValue(e.target.value)}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <TextField
                  select
                  fullWidth
                  label="Outside rent term"
                  value={outsideCycle}
                  onChange={(e) => setOutsideCycle(e.target.value)}
                >
                  <MenuItem value="daily">Days</MenuItem>
                  <MenuItem value="weekly">Weeks</MenuItem>
                  <MenuItem value="monthly">Months</MenuItem>
                </TextField>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <TextField
                  type="number"
                  fullWidth
                  label={durationLabel(outsideCycle)}
                  value={outsideDuration}
                  onChange={(e) => setOutsideDuration(Math.max(1, parseInt(e.target.value, 10) || 1))}
                  inputProps={{ min: 1 }}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <TextField
                  type="number"
                  fullWidth
                  required
                  label="Outside rent (we pay)"
                  value={outsideRent}
                  onChange={(e) => setOutsideRent(e.target.value)}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <TextField
                  type="number"
                  fullWidth
                  required
                  label={`Customer price (${rateLabel(billingCycle).toLowerCase()})`}
                  value={periodRate}
                  onChange={(e) => setPeriodRate(e.target.value)}
                  helperText="Rent we charge the customer"
                />
              </Grid>
              <Grid item xs={12} md={10}>
                <TextField
                  fullWidth
                  multiline
                  minRows={2}
                  label="Components"
                  placeholder="Charger, bag, extra battery, cables…"
                  value={outsideComponents}
                  onChange={(e) => setOutsideComponents(e.target.value)}
                />
              </Grid>
              <Grid item xs={12} md={2}>
                <Button variant="outlined" fullWidth onClick={() => addItem(null)}>Add item</Button>
              </Grid>
            </>
          ) : (
            <>
              <Grid item xs={12} md={4}>
                <Autocomplete
                  freeSolo
                  options={serialHits}
                  getOptionLabel={(o) => (typeof o === 'string' ? o : o.serial_number || '')}
                  inputValue={serialQuery}
                  onInputChange={(_, v) => setSerialQuery(v)}
                  onChange={(_, v) => {
                    if (v && typeof v === 'object') {
                      setManualSerial(v.serial_number);
                      setPickedProduct(v.product || null);
                      if (periodRate === '') setPeriodRate(v.product?.price || '');
                    }
                  }}
                  renderInput={(params) => (
                    <TextField {...params} label="Serial number" placeholder="Type at least 3 characters" />
                  )}
                  renderOption={(props, option) => (
                    <li {...props} key={option.id || option.serial_number}>
                      {option.serial_number} — {option.product?.name || 'Unknown'} ({option.status || 'n/a'})
                    </li>
                  )}
                />
              </Grid>
              <Grid item xs={12} md={3}>
                <Autocomplete
                  options={products}
                  getOptionLabel={(p) => p ? `${p.name} (${p.product_code || ''})` : ''}
                  value={pickedProduct}
                  onChange={(_, v) => setPickedProduct(v)}
                  renderInput={(params) => <TextField {...params} label="Product (if no serial match)" />}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={2}>
                <TextField
                  fullWidth
                  label="Serial (manual)"
                  value={manualSerial}
                  onChange={(e) => setManualSerial(e.target.value)}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={2}>
                <TextField
                  type="number"
                  fullWidth
                  label={rateLabel(billingCycle)}
                  value={periodRate}
                  onChange={(e) => setPeriodRate(e.target.value)}
                />
              </Grid>
              <Grid item xs={12} md={1}>
                <Button variant="outlined" onClick={() => addItem(null)}>Add</Button>
              </Grid>
            </>
          )}
        </Grid>

        <Table size="small" sx={{ ...tableSx, mt: 2 }}>
          <TableHead>
            <TableRow>
              <TableCell>Item</TableCell>
              <TableCell>Serial</TableCell>
              {rentalSource === 'outside' ? <TableCell>Purchase</TableCell> : null}
              {rentalSource === 'outside' ? <TableCell>Asset value</TableCell> : null}
              {rentalSource === 'outside' ? <TableCell>Outside rent</TableCell> : null}
              {rentalSource === 'outside' ? <TableCell>Components</TableCell> : null}
              <TableCell align="right">{rentalSource === 'outside' ? 'Customer price' : 'Period rate'}</TableCell>
              <TableCell />
            </TableRow>
          </TableHead>
          <TableBody>
            {items.length === 0 ? (
              <TableRow>
                <TableCell colSpan={rentalSource === 'outside' ? 8 : 4}>
                  <Typography color="text.secondary">No items added.</Typography>
                </TableCell>
              </TableRow>
            ) : items.map((it, idx) => (
              <TableRow key={`${it.serial_number}-${idx}`}>
                <TableCell>{it.product_name}</TableCell>
                <TableCell>{it.serial_number || '—'}</TableCell>
                {rentalSource === 'outside' ? (
                  <TableCell>
                    {it.purchase_date || '—'}
                    {it.purchase_price != null ? ` · ${money(it.purchase_price)}` : ''}
                  </TableCell>
                ) : null}
                {rentalSource === 'outside' ? <TableCell>{money(it.asset_value)}</TableCell> : null}
                {rentalSource === 'outside' ? (
                  <TableCell>
                    {money(it.outside_rent)}
                    {it.outside_duration ? ` · ${it.outside_duration} ${it.outside_cycle || ''}` : ''}
                  </TableCell>
                ) : null}
                {rentalSource === 'outside' ? <TableCell>{it.components || '—'}</TableCell> : null}
                <TableCell align="right">{money(it.period_rate)}</TableCell>
                <TableCell align="right">
                  <IconButton size="small" onClick={() => setItems(items.filter((_, i) => i !== idx))}>
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </RentalSection>

      <Paper sx={{
        p: 2, mb: 2, borderRadius: 2.5, border: '1px solid', borderColor: 'divider',
        display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 2,
        bgcolor: (t) => (t.palette.mode === 'dark' ? 'rgba(66,165,245,0.08)' : '#e3f2fd')
      }}>
        <Box>
          <Typography variant="caption" color="text.secondary" fontWeight={700}>Contract total</Typography>
          <Typography variant="h6" fontWeight={800}>
            {Math.max(1, duration)} invoice{Math.max(1, duration) === 1 ? '' : 's'} × {money(periodTotal)} = {money(contractTotal)}
          </Typography>
          {Number(deposit) > 0 ? (
            <Typography variant="body2" color="text.secondary">Deposit {money(deposit)}</Typography>
          ) : null}
        </Box>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button onClick={() => navigate('/rentals')}>Cancel</Button>
          <Button variant="contained" size="large" onClick={save} disabled={saving}>
            {saving ? 'Saving…' : 'Create rental'}
          </Button>
        </Box>
      </Paper>

      <Dialog open={newOpen} onClose={() => setNewOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle>New rental customer</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 0.5 }}>
            <Grid item xs={12}>
              <TextField
                autoFocus
                fullWidth
                required
                label="Name"
                value={newCustomer.name}
                onChange={(e) => setNewCustomer({ ...newCustomer, name: e.target.value })}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Phone"
                value={newCustomer.phone}
                onChange={(e) => setNewCustomer({ ...newCustomer, phone: e.target.value })}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Email"
                value={newCustomer.email}
                onChange={(e) => setNewCustomer({ ...newCustomer, email: e.target.value })}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Company"
                value={newCustomer.company_name}
                onChange={(e) => setNewCustomer({ ...newCustomer, company_name: e.target.value })}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Address"
                value={newCustomer.address}
                onChange={(e) => setNewCustomer({ ...newCustomer, address: e.target.value })}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setNewOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={saveNewCustomer} disabled={savingCustomer}>
            {savingCustomer ? 'Saving…' : 'Save customer'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default RentalForm;
